/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ViewEncapsulation,
  HostListener,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import {
  Pne,
  SearchService,
} from 'src/app/shared/services/metadata-api/search.service';
import { RoutingService } from 'src/app/service/routing.service';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject, empty, of } from 'rxjs';
import {
  map,
  debounceTime,
  shareReplay,
  catchError,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { AiSearchDialogComponent } from './ai-search-dialog/ai-search-dialog.component';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';
import {
  AiSearchRecent,
  AiSearchRecentsService,
} from 'src/app/shared/services/ai-search/ai-search-recents.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { ConfigService } from 'src/app/service/config.service';
import { UserActivityApiService } from 'src/app/shared/services/customer-api/user-activity-api.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { buildTextSearchQuery } from 'src/app/shared/utils/text-query.utils';
import { isMacPlatform } from 'src/app/shared/utils/platform.utils';

const FULL_LABEL = 'Search by Name, ISIN or D-U-N-S Number';
const MOBILE_LABEL = 'Search...';

const EMPTY_SEARCH_RESULT = {
  entity_prefix_search: { hits: [] },
  event_search: { hits: [] },
  source_prefix_search: { hits: [] },
};

// The only children news/market actually has (app-routing.module.ts). Anything
// else — a portfolio-only view like scanner/main/benchmark, or a segment from
// invest/chat/reports — has no market equivalent.
const MARKET_VIEWS = ['chart', 'audit'];

/**
 * The view to open an entity in: the one currently on screen when it exists
 * under news/market, else the chart. An allowlist rather than a list of views to
 * rewrite, so a view added elsewhere in the app can never route to a 404 here.
 */
function marketView(currentView: string): string {
  return MARKET_VIEWS.includes(currentView) ? currentView : 'chart';
}

/**
 * Entity keys from the activity log, newest first. The log records one row per
 * click, so the same entity repeats — keep the first (most recent) occurrence.
 */
function recentEntityKeys(activity: unknown): string[] {
  if (!Array.isArray(activity)) return [];
  const seen = new Set<string>();
  return activity
    .filter((row) => row?.clicked?.field === 'entity')
    .map((row) => row.clicked.value as string)
    .filter((key) => {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/**
 * Nothing matched, and the text is not a boolean expression — the panel offers
 * the raw text as a query instead. A leading `(` means the user is writing a
 * query on purpose, so an empty hit list is not a failed lookup.
 */
function isNoMatch(
  result: { [group: string]: { hits: unknown[] } },
  value: string,
): boolean {
  if (!result) return false;
  // Entity hits only — the same count the "N results" label shows. Source and
  // event hits are still returned by the API but no longer rendered, so counting
  // them left the panel looking empty with the AI option pushed to the bottom.
  return (
    result.entity_prefix_search.hits.length === 0 && value.charAt(0) !== '('
  );
}

/**
 *
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-menu-search',
  templateUrl: './menu-search.component.html',
  styleUrls: ['./menu-search.component.scss'],
})
export class MenuSearchComponent implements OnDestroy {
  /**
   * triggering MatAutocomplete panel for input
   */
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger })
  inputAutoComplete: MatAutocompleteTrigger;

  /**
   * needed to blur input on dialog open
   */
  @ViewChild('inputAutoComplete') inputElement: ElementRef;

  /**
   * query parameters url
   */
  params: any;

  /**
   * data from the search request
   */
  items$: Observable<{
    entity_prefix_search: { hits: any[] };
    event_search?: { hits: any[] };
    source_prefix_search: { hits: any[] };
  }>;

  /**
   * data from the pnes search request
   */
  itemsPNE: Pne[] = [];

  /**
   * saved entities from previous searches
   */
  previousSearches = [];

  /**
   * searches generated in the AI-search dialog, listed above the entities
   */
  aiRecents: AiSearchRecent[] = [];

  /**
   * whether the current search returned entity/event/source hits. Drives where
   * the AI option sits: below real results, on top when there are none.
   */
  hasSearchResults = false;

  /**
   * form control search
   */
  mysearch: UntypedFormControl = new UntypedFormControl();
  lastTypedSearch = '';

  /**
   * OS-aware hint shown in the search bar for the focus shortcut.
   */
  readonly shortcutLabel = isMacPlatform() ? '⌘K' : 'Ctrl+K';

  /**
   * loading
   */
  loading: boolean;

  /**
   * loading pnes
   */
  loadingPNE: boolean;

  /**
   * ready to start
   */
  mystart: boolean;

  /**
   * empty input field
   */
  emptyInput = true;

  /**
   * whether the dropdown is showing. Drives the box's open styling, and comes
   * from the autocomplete's own open/close events rather than focus: clicking
   * inside the panel moves focus out of the box while the panel stays open.
   */
  panelOpen = false;

  /**
   * placeholder shown in the input; shortened on mobile
   */
  label = FULL_LABEL;

  /**
   * show more entities from a group
   */
  showMoreEntities = {};

  /**
   * show more pnes from a group
   */
  showMorePNEs = false;

  /**
   * color theme
   */
  theme = 'dark';

  screenSize$ = this.breakpointService.screenSize$;
  isMobile = false;

  private destroy$ = new Subject<void>();

  /**
   * queryParams and valueChanges subscribe
   */
  constructor(
    private entityInfoService: EntityInfoService,
    private searchService: SearchService,
    private router: Router,
    private route: ActivatedRoute,
    public routing: RoutingService,
    public dialog: MatDialog,
    public featureFlagsService: FeatureFlagsService,
    public config: ConfigService,
    private breakpointService: BreakpointService,
    private aiSearchService: AiSearchService,
    private aiRecentsService: AiSearchRecentsService,
    private userActivityApi: UserActivityApiService,
  ) {
    // Live rather than take(1): the width was sampled once at construction, so
    // the short placeholder never appeared after a resize — and never went back.
    this.breakpointService.screenSize$
      .pipe(takeUntil(this.destroy$))
      .subscribe((screen) => {
        this.isMobile = screen.isMobile;
        this.label = screen.isMobile ? MOBILE_LABEL : FULL_LABEL;
      });
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.params = params;
        this.theme = this.routing.theme;
        this.showMoreEntities = {};
        this.showMorePNEs = false;
      });
    this.mysearch.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value || value.length < 2) {
          this.clearSearch();
          return;
        }
        this.emptyInput = false;
        this.mystart = true;
        this.loading = true;
        this.loadingPNE = true;
        this.fetchPnes(value);
        // Side effects live in tap and the projection in map: the flags used to
        // be assigned inside map, so whether they were set at all depended on
        // something subscribing, and reading the pipeline told you nothing
        // about when they changed.
        this.items$ = this.searchService.searchMeta(value, this.params).pipe(
          catchError(() => of(EMPTY_SEARCH_RESULT)),
          tap((result) => {
            this.loading = false;
            this.hasSearchResults = !isNoMatch(result, value);
          }),
          map((result) =>
            isNoMatch(result, value)
              ? { ...result, parse_boolean_query: buildTextSearchQuery(value) }
              : result,
          ),
          shareReplay(),
        );
      });
  }

  /**
   * Potential named entities load alongside the entity search rather than from
   * inside its map operator, where the request only fired if someone happened
   * to subscribe to items$.
   */
  private fetchPnes(value: string) {
    this.searchService
      .searchPne(value, this.params)
      .pipe(
        catchError(() => of([] as Pne[])),
        takeUntil(this.destroy$),
      )
      .subscribe((pnes) => {
        this.itemsPNE = pnes;
        this.loadingPNE = false;
      });
  }

  /** Input too short to search on. */
  private clearSearch() {
    this.emptyInput = true;
    this.mystart = false;
    this.loading = false;
    this.loadingPNE = false;
    this.hasSearchResults = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ⌘K / Ctrl+K focuses the search and opens its dropdown. Bound on document
   * because the host is a small strip in the nav bar; the visibility guard keeps
   * a second, hidden instance (the mobile bar) from stealing the shortcut.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    if (event.key?.toLowerCase() !== 'k') return;
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
      return;
    }
    const input = this.inputElement?.nativeElement;
    if (!input || input.offsetParent === null) return;
    event.preventDefault();
    input.focus();
    this.inputAutoComplete?.openPanel();
  }

  /**
   * runs on input focus to get & display previous searches
   */
  onFocus() {
    if (this.mysearch.value && this.mysearch.value.length > 1) {
      this.mystart = true;
      this.loading = false;
    } else {
      this.mystart = false;
      this.loading = true;
      this.aiRecents = this.aiRecentsService.list();
      // switchMap rather than a nested subscribe: two quick focuses used to race,
      // and whichever response landed last won regardless of which was asked for
      // last.
      this.userActivityApi
        .getSearchActivity(10)
        .pipe(
          map((activity) => recentEntityKeys(activity)),
          switchMap((keys) =>
            keys.length
              ? this.entityInfoService
                  .getEntitiesInfo(keys)
                  .pipe(map((byKey) => ({ keys, byKey })))
              : of({ keys, byKey: {} }),
          ),
          takeUntil(this.destroy$),
        )
        .subscribe(({ keys, byKey }) => {
          // Indexed by key, never Object.values(): the map is keyed by
          // compound_key in response order, and getEntitiesInfo caches on the
          // sorted key list — so reading values gave an order unrelated to
          // recency, and a cache hit replayed it even after a newer click.
          this.previousSearches = keys
            .map((key) => byKey[key])
            .filter((entity) => !!entity)
            .map((entity) => ({ entity }));
          this.loading = false;
        });
    }
  }

  /**
   * redirect specific select type
   */
  openAiSearch() {
    this.aiSearchService.pendingLegalName = this.lastTypedSearch.trim() || null;
    this.lastTypedSearch = '';
    this.mystart = false;
    this.mysearch.reset();
    this.inputElement.nativeElement.blur();
    this.items$ = empty();
    this.inputAutoComplete.closePanel();
    this.dialog.open(AiSearchDialogComponent);
  }

  /**
   * clears the input and both result lists after a pick
   */
  private resetPanel() {
    this.mystart = false;
    this.mysearch.reset();
    this.inputElement.nativeElement.blur();
    this.items$ = empty();
    this.previousSearches = [];
    this.aiRecents = [];
  }

  goSelect(option) {
    if (option === 'aiSearch') {
      this.openAiSearch();
      return;
    }
    if (option?.type === 'aiRecent') {
      this.resetPanel();
      this.aiSearchService.launchAiSearch(option.recent.data);
      return;
    }
    // Entity hits (including their children and the previous-searches rows) and
    // potential named entities are the only options this panel offers. The
    // source, event and query branches that used to live here were orphaned when
    // YT-6069 deleted their (already commented-out) markup, and both pointed at
    // routes that no longer exist — news/market/radar and news/search/benchmark.
    const target = option?.entity
      ? { type: option.entity.type, id: option.entity.alpha_id }
      : option?.type === 'pne'
        ? { type: 'pne', id: option.pne.alpha_id }
        : null;
    if (!target) {
      return;
    }

    this.resetPanel();
    this.router.navigate(
      [`news/market/${marketView(this.routing.getPathSegment(4))}`],
      {
        relativeTo: this.route,
        queryParams: {
          id: target.id,
          type: target.type,
          eventid: null,
          eventype: null,
          scoreevent: null,
          tag: null,
          participant: null,
          newstype: null,
          newsaudit: null,
          portfolioId: null,
          groupId: null,
          eventsFromScanner: null,
          score: null,
        },
        queryParamsHandling: 'merge',
      },
    );
    this.logSearchClick(option, target);
  }

  private logSearchClick(
    option: { pne?: { surface: string; compound_key: string } },
    target: { type: string; id: string },
  ) {
    if (!this.featureFlagsService.featureFlags.activityLogging) {
      return;
    }
    const isPne = target.type === 'pne';
    this.userActivityApi
      .userActivity({
        environment: this.config.appConfig.routing.project,
        url: this.router.url,
        activity_type: 'search_bar_click',
        clicked: {
          field: isPne ? 'potential_named_entity' : 'entity',
          // Only the entity form is read back — onFocus filters the log on
          // field === 'entity' and looks the value up as a compound_key.
          value: isPne
            ? JSON.stringify({
                surface: option.pne.surface,
                compound_key: option.pne.compound_key,
              })
            : `${target.type}:${target.id}`,
        },
      })
      .subscribe();
  }

  /**
   *
   */
  iMatch(entity) {
    if (entity.matched_form) {
      return (
        entity.name.charAt(0).toLowerCase() !== entity.matched_form.charAt(0)
      );
    }
  }

  /**
   * format big numbers >999 into K, M, ...
   */
  numberFormat(num) {
    // @ts-ignore
    return Intl.NumberFormat('en', { notation: 'compact' }).format(num);
  }

  /**
   * show more than 5 entities for a particular group
   */
  expandEntitiesGroup(i) {
    this.showMoreEntities[i] = !this.showMoreEntities[i];
  }

  /**
   * check whether to show more than 5 entities for a particular group
   */
  checkShowMoreEntities(i) {
    if (this.showMoreEntities[i]) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * show more than 5 pnes
   */
  expandPNEsGroup() {
    this.showMorePNEs = !this.showMorePNEs;
  }

  /**
   * check whether to show more than 5 pnes
   */
  checkShowMorePNEs() {
    if (this.showMorePNEs) {
      return 1000;
    } else {
      return 5;
    }
  }
}
